/* eslint no-underscore-dangle: 0 */
import { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Client } from '@twilio/conversations';

import {
  fetchTwilioTokenWithChatId,
  sendMessage,
  markAsRead,
  fetchCouplesGuestChatToken,
} from '../../../../../actions/messaging';
import Messages from './Messages';
import MessageForm from './MessageForm';

function Chat({
  appointment = {},
  fetchTwilioTokenWithChatId: fetchTwilioTokenWithChatIdAction,
  markAsRead: markAsReadAction,
  sendMessage: sendMessageAction,
  timezone,
  token = {},
  user = {},
  fetchCouplesGuestChatToken: fetchCouplesGuestChatTokenAction,
}) {
  const [messages, setMessages] = useState([]);
  const [channel, setChannel] = useState(null);
  const [chat, setChat] = useState(null);
  const [isCouplesGuest, setIsCouplesGuest] = useState(false);
  const chatClientRef = useRef(null);
  const isMountedRef = useRef(false);
  const stateRef = useRef({ messages, channel, chat, isCouplesGuest });
  const propsRef = useRef({
    appointment,
    token,
    markAsReadAction,
    sendMessageAction,
    fetchTwilioTokenWithChatIdAction,
    fetchCouplesGuestChatTokenAction,
  });
  stateRef.current = { messages, channel, chat, isCouplesGuest };
  propsRef.current = {
    appointment,
    token,
    markAsReadAction,
    sendMessageAction,
    fetchTwilioTokenWithChatIdAction,
    fetchCouplesGuestChatTokenAction,
  };

  const addMessage = (message, mark = false) => {
    const { isCouplesGuest: couplesGuest } = stateRef.current;
    if (isMountedRef.current) {
      const messageData = {
        ...message,
        me: message.author === propsRef.current.token.identity,
      };

      setMessages(prev => [...prev, messageData]);
      // Preserve class setState callback timing.
      Promise.resolve().then(() => {
        if (!couplesGuest && !messageData.me && mark) {
          propsRef.current.markAsReadAction({
            id: stateRef.current.chat.id,
          });
        }
      });
    }
  };

  const setupChannel = nextChannel => {
    nextChannel.getMessages().then(channelMessages => {
      const totalMessages = channelMessages.items.length;
      for (let message = 0; message < totalMessages; message += 1) {
        addMessage(channelMessages.items[message].state, false);
      }
    });

    nextChannel.on('messageAdded', ({ author, body, dateCreated }) => {
      addMessage({ author, body, timestamp: dateCreated }, true);
    });
  };

  const rejoinChannel = chatClient => {
    const {
      appointment: { chat: appointmentChat },
    } = propsRef.current;

    setChat(appointmentChat);
    propsRef.current.markAsReadAction({ id: appointmentChat.id });
    const promise = chatClient.getConversationBySid(appointmentChat.channel);
    promise
      .then(joinedChannel => {
        setChannel(joinedChannel);
        setupChannel(joinedChannel);
      })
      .catch(() => {});
  };

  const initCouplesGuestChat = () => {
    const { appointment: currentAppointment } = propsRef.current;

    propsRef.current.fetchCouplesGuestChatTokenAction(
      currentAppointment.id,
      (tokenData, error) => {
        if (!error) {
          if (chatClientRef.current) {
            try {
              chatClientRef.current.removeAllListeners();
              chatClientRef.current.shutdown();
            } catch (shutdownError) {
              // ignore
            }
          }
          const chatClient = new Client(tokenData.id);
          chatClientRef.current = chatClient;
          chatClient.on('connectionError', () => {});
          chatClient.on('stateChanged', state => {
            if (state === 'initialized') {
              chatClient
                .getConversationBySid(tokenData.channel_id)
                .then(joinedChannel => {
                  if (isMountedRef.current) {
                    setChannel(joinedChannel);
                  }
                  setupChannel(joinedChannel);
                })
                .catch(() => {});
            }
          });
        }
      },
    );
  };

  const initChats = () => {
    const {
      appointment: { chat: appointmentChat },
    } = propsRef.current;
    const chatId = appointmentChat.id;

    propsRef.current.fetchTwilioTokenWithChatIdAction(
      chatId,
      tokenResponse => {
        const tokenData = tokenResponse.data;
        if (chatClientRef.current) {
          try {
            chatClientRef.current.removeAllListeners();
            chatClientRef.current.shutdown();
          } catch (shutdownError) {
            // ignore
          }
        }
        const chatClient = new Client(tokenData.id);
        chatClientRef.current = chatClient;
        chatClient.on('connectionError', () => {});
        chatClient.on('stateChanged', state => {
          if (state === 'initialized') {
            rejoinChannel(chatClient);
          }
        });
      },
    );
  };

  useEffect(() => {
    isMountedRef.current = true;

    if (appointment.id && user.id) {
      if (
        appointment.provider_type === 'couples' &&
        user.id === appointment.couples_appointment_detail.guest.id
      ) {
        setIsCouplesGuest(true);
        stateRef.current.isCouplesGuest = true;
        initCouplesGuestChat();
      } else {
        initChats();
      }
    }

    return () => {
      isMountedRef.current = false;
      if (stateRef.current.channel) {
        stateRef.current.channel.removeAllListeners('messageAdded');
      }
      if (chatClientRef.current) {
        try {
          chatClientRef.current.removeAllListeners();
          chatClientRef.current.shutdown();
        } catch (error) {
          // Client may already be shut down.
        }
        chatClientRef.current = null;
      }
    };
    // Match class componentDidMount / WillUnmount lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewMessage = text => {
    const {
      channel: currentChannel,
      chat: currentChat,
      isCouplesGuest: couplesGuest,
    } = stateRef.current;

    if (currentChannel) {
      if (!couplesGuest) {
        propsRef.current.sendMessageAction({
          id: currentChat.id,
          message: text,
        });
      }

      currentChannel.sendMessage(text);
    }
  };

  const receivers = [];

  if (appointment.provider_type === 'couples') {
    receivers.push(appointment.couples_appointment_detail.provider);
    if (isCouplesGuest) {
      receivers.push(appointment.couples_appointment_detail.host);
    } else {
      receivers.push(appointment.couples_appointment_detail.guest);
    }
  } else {
    receivers.push(appointment.counsellor);
  }

  return (
    <div className="sidebar-chat-container sidebar-flex">
      <Messages messages={messages} timezone={timezone} />
      <MessageForm
        connected={channel !== null}
        handleNewMessage={handleNewMessage}
      />
    </div>
  );
}

Chat.propTypes = {
  appointment: PropTypes.object,
  fetchTwilioTokenWithChatId: PropTypes.func.isRequired,
  markAsRead: PropTypes.func.isRequired,
  sendMessage: PropTypes.func.isRequired,
  timezone: PropTypes.string.isRequired,
  token: PropTypes.object,
  user: PropTypes.object,
  fetchCouplesGuestChatToken: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    user: state.user,
    token: state.misc.token,
    chats: state.chats,
    timezone: _.get(state, 'user.preference.timezone', 'America/Toronto'),
    appointment: state.session.appointment,
  };
}

export default connect(mapStateToProps, {
  fetchTwilioTokenWithChatId,
  sendMessage,
  markAsRead,
  fetchCouplesGuestChatToken,
})(Chat);
